import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { DateTime } from 'luxon';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatDateWithOptions as formatDateLocalized } from '@cf-shared/date-format';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { LoadingController } from 'providers/loading-controller';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper, DEVICE_LOCAL_DOC_IDS, DocWatcher } from 'services/database-wrapper';
import { withoutDeviceLocalDocs } from 'services/database-wrapper-passthrough';
import { LoggingService } from 'services/logging.service';
import { handleRestoreFailureAsync, RestoreDeps, runGuardedRestoreAsync } from 'services/restore-failure';
import { exportFileAsync } from 'services/system-share';
import { PouchDoc } from '@cf-shared/data-model/pouch';
import { getMyApp } from 'app/app-interface';
import { DbSnapshotsPageParams } from './db-snapshots-interface';

export interface DbSnapshot extends PouchDoc {
  name: string;
  createdAt_utc: string;
  docCount: number;
  data: any[];
}

@Component({
    selector: 'page-db-snapshots',
    templateUrl: 'db-snapshots.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DbSnapshotsPage extends CsPage<DbSnapshotsPageParams> implements OnDestroy {
  protected LL = LL;
  protected snapshots: DbSnapshot[] = [];
  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private popupCtrl: PopupMenuProvider,
    private logging: LoggingService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdRef.markForCheck();
      });
    }
    await this.loadSnapshotsAsync();
  }

  handleCreateSnapshot() {
    void this.createSnapshotAsync();
  }

  handleMoreOptions(event: Event, snapshot: DbSnapshot) {
    void this.showMoreOptionsAsync(event, snapshot);
  }

  formatDate(snapshot: DbSnapshot): string {
    return formatDateLocalized(DateTime.fromISO(snapshot.createdAt_utc).toLocal(), BOOT_LOCALE, DateTime.DATETIME_MED);
  }

  // ---

  private async showMoreOptionsAsync(event: Event, snapshot: DbSnapshot) {
    const buttons: PopupMenuButton[] = [
      {
        text: LL.dbSnapshots.rename(),
        icon: 'pencil',
        handler: () => void this.renameSnapshotAsync(snapshot),
      },
      {
        text: LL.dbSnapshots.download(),
        icon: 'download',
        handler: () => void this.downloadSnapshot(snapshot),
      },
      {
        text: LL.dbSnapshots.restore(),
        icon: 'refresh',
        handler: () => void this.restoreSnapshotAsync(snapshot),
      },
      {
        text: LL.common.buttons.delete(),
        icon: 'trash',
        handler: () => void this.deleteSnapshotAsync(snapshot),
      },
    ];

    await this.popupCtrl.createMenu({
      buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  private async downloadSnapshot(snapshot: DbSnapshot) {
    const filename = snapshot.name.replace(/[^a-zA-Z0-9_-]/g, '_') + '.json';
    // hand the file to the user (browser download, or the share sheet in the native apps)
    try {
      await exportFileAsync({ data: JSON.stringify(snapshot.data, null, 2), filename, mimeType: 'application/json' });
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.errors.userFacing.exportFailed());
    }
  }

  private async loadSnapshotsAsync() {
    const docs = await this.userDb.database.getByIdHeadAsync('snapshot/', this.docWatcher);
    this.snapshots = (docs as DbSnapshot[]).sort((a, b) => b.createdAt_utc.localeCompare(a.createdAt_utc));
    this.safeMarkForCheck(this.cdRef);
  }

  private async createSnapshotAsync() {
    const loading = this.loadingCtrl.create({ content: LL.dbSnapshots.creating() });
    void loading.present();

    try {
      const allDocs = await this.userDb.database.getAllDataUncachedAsync();

      // filter out snapshot docs and design docs — and every device-local doc (#4798). A
      // `snapshot/` document is an ordinary replicating document, so anything copied INSIDE it
      // reaches the server even if nobody ever restores the snapshot; taking a snapshot is
      // therefore one of the paths that has to consult the device-local list itself.
      const userDocs = withoutDeviceLocalDocs(
        allDocs.filter(doc =>
          !doc._id.startsWith('snapshot/') && !doc._id.startsWith('_design/')
        ),
        DEVICE_LOCAL_DOC_IDS,
      );

      // strip PouchDB metadata
      const cleanDocs = userDocs.map(doc => {
        const clean = { ...doc };
        delete clean._rev;
        delete clean._conflicts;
        delete clean.db__trans;
        return clean;
      });

      const now = DateTime.utc();
      const snapshot: DbSnapshot = {
        _id: 'snapshot/' + now.toISO(),
        name: 'Snapshot ' + formatDateLocalized(now.toLocal(), BOOT_LOCALE, DateTime.DATETIME_MED),
        createdAt_utc: now.toISO(),
        docCount: cleanDocs.length,
        data: cleanDocs,
      } as DbSnapshot;

      const transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.database.saveRecordAsync(snapshot, transaction);
      await this.loadSnapshotsAsync();
    } finally {
      void loading.dismiss();
    }
  }

  private async renameSnapshotAsync(snapshot: DbSnapshot) {
    const newName = await this.alertCtrl.showAlertAsync<string>({
      title: LL.dbSnapshots.renameTitle(),
      inputs: [
        {
          name: 'name',
          type: 'text',
          value: snapshot.name,
          placeholder: LL.dbSnapshots.renamePlaceholder(),
        },
      ],
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
        {
          text: LL.common.buttons.save(),
          role: AlertButtonRole.Primary,
          result: (data: any) => data.name as string,
        },
      ],
    });

    if (newName && newName !== snapshot.name) {
      snapshot.name = newName;
      const transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.database.saveRecordAsync(snapshot, transaction);
      this.safeMarkForCheck(this.cdRef);
    }
  }

  private async restoreSnapshotAsync(snapshot: DbSnapshot) {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.dbSnapshots.restoreConfirmTitle(),
      message: LL.dbSnapshots.restoreConfirmMessage({ name: snapshot.name }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
        {
          text: LL.dbSnapshots.restore(),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });

    if (!confirmed) return;

    // the loading cover and the guarantee that it always comes down live in restore-failure.ts, so
    // this page and the JSON restore page cannot drift apart on them
    const restoreError = await runGuardedRestoreAsync(this.restoreDeps(), {
      docs: snapshot.data,
      loadingContent: LL.dbSnapshots.restoring(),
    });

    if (restoreError) {
      await this.handleRestoreFailureAsync(restoreError, snapshot.name);
      return;
    }

    // set flag so we show a confirmation alert after reload
    sessionStorage.setItem('snapshot_restored', snapshot.name);

    // Reload to the root page. `resetToRoot` is what forgets the remembered warm-resume address, so
    // the next boot cannot reopen a page describing data this restore has just replaced (#5486).
    // reloadAppAsync NEVER returns -- it always reaches location.reload().
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  /** What this page's restore needs from the app, handed to the shared restore routines. */
  private restoreDeps(): RestoreDeps {
    return {
      database: this.userDb.database,
      alertCtrl: this.alertCtrl,
      loadingCtrl: this.loadingCtrl,
      logging: this.logging,
    };
  }

  /** Tell the user the restore failed, and reload or deliberately do not — the rules themselves, and
   * the long explanation of why a failure AFTER the remote was replaced must NOT reload, live in
   * `services/restore-failure.ts`. This page supplies only its own telemetry name, its own report
   * context and its own translated wording. */
  private async handleRestoreFailureAsync(err: any, snapshotName: string) {
    await handleRestoreFailureAsync(this.restoreDeps(), {
      err,
      problemName: 'DbSnapshotRestoreFailed',
      context: { snapshotName },
      messages: {
        failedTitle: LL.dbSnapshots.restoreFailedTitle(),
        failed: LL.dbSnapshots.restoreFailed(),
        cleanupFailed: LL.dbSnapshots.restoreCleanupFailed(),
        okButton: { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary },
      },
    });
  }

  private async deleteSnapshotAsync(snapshot: DbSnapshot) {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.dbSnapshots.deleteConfirmTitle(),
      message: LL.dbSnapshots.deleteConfirmMessage({ name: snapshot.name }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
        {
          text: LL.common.buttons.delete(),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });

    if (confirmed) {
      const transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.database.deleteRecordAsync(snapshot, transaction);
      await this.loadSnapshotsAsync();
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }
}
